import { AIDE_URL } from '@/common/const/env'
import { getLocale } from '@/i18n'
import { getTheme } from '@/utils/app-theme'
import { getElColorPrimary } from '@/utils/theme-color'
import type { PermissionMap } from '@/services/service-permission.service'
import { servicePermission, team, user } from '@/common/basis/root-injector'
import { resolveAiAssistantSubscriptionExpireAt } from '@/views/dashboard/service/service-subscribe/ai-assistant-detail/ai-assistant.service'
import { useAideDrawerStore } from '@/stores/aide-drawer.store'

/** 宿主业务标识（与龙虾 aide-key 配置对齐） */
export const AIDE_KEY = {
  DUBATEAM: 'dubateam',
} as const

/** 宿主 × 助手 iframe 通信 channel（两边需保持一致） */
export const BRIDGE_CHANNEL = 'aide-bridge'

export const BRIDGE_VERSION = 1

/** 助手 iframe 侧固定标识（协议值，与龙虾 aide-bridge 对齐） */
export const BRIDGE_AIDE_PARTY = 'aide'

/** 宿主传入的主题色（各集成方按当前 UI 主题同步） */
export interface AideThemeColors {
  /** 主题主色（用户肉眼所见），支持 #RRGGBB 或 rgb()/rgba() */
  primary?: string | null
  /** 主色上的前景色（按钮文字等）；未传时默认白色 */
  primaryForeground?: string | null
  /** 明暗模式，供助手自身 light/dark class 使用 */
  mode?: 'light' | 'dark'
}

/** 毒霸团队版宿主业务字段 */
export interface DubateamBridgeData {
  token?: string | null
  teamId?: number | null
  /** OAuth 登录校验用 */
  uid?: string | null
  /** AI 助手订阅到期时间（Unix 秒），同步给龙虾顶栏展示 */
  aiSubscriptionExpireAt?: number | null
}

/** 通信数据体：通用字段 + 各宿主业务命名空间（dubateam 等） */
export interface BridgeData {
  /** 宿主业务标识，如 dubateam */
  aideKey?: string | null
  /** 宿主主题色，供 iframe 内助手 UI 跟随 */
  theme?: AideThemeColors | null
  /** 宿主界面语言，如 zh-CN / zh-TW / en-US / ja-JP */
  locale?: string | null
  /** 毒霸团队版业务字段 */
  dubateam?: DubateamBridgeData | null
  /** 仅首次挂载 iframe 时为 true，触发登录与 init-duba-team；抽屉开关不再携带 */
  forceBoot?: boolean | null
  /** 打开抽屉时拉实例列表：运行中则不动，否则 init 并轮询到运行中 */
  checkInstances?: boolean | null
  [key: string]: unknown
}

export interface BridgeEnvelope {
  channel: typeof BRIDGE_CHANNEL
  version: typeof BRIDGE_VERSION
  from: string
  type: string
  data?: BridgeData
  requestId?: string
}

export const BridgeMessageType = {
  Ready: 'aide:ready',
  Sync: 'aide:sync',
} as const

type BridgeHandler = (data: BridgeData, envelope: BridgeEnvelope) => void

export interface AideBridgeServiceOptions {
  getData: () => BridgeData | null
  targetOrigin?: string
}

interface DubateamUserInfo {
  token?: string
  uid?: string
}

interface DubateamCurrentTeam {
  team_id?: number
}

/**
 * 团队权益 map：取权限服务的当前快照。
 * ServicePermissionService 自身负责从 localStorage.rightsMap 初始化，
 * 并在 refreshCurrentTeamPermission() / updateRightsMap() 时同步更新 rightsMapData，
 * 这里不再自行解析 localStorage。
 */
function readTeamRightsMap (): PermissionMap {
  return servicePermission.rightsMapData || {}
}

/** 读取当前宿主上下文，供 bridge 同步到助手 */
export function resolveAideBridgeData (): BridgeData | null {
  // 取页面运行态而非 Cookie：Cookie 跨标签共享，多标签各自切换团队时会互相覆盖
  const userInfo = (user.getUserInfo() || {}) as DubateamUserInfo
  const currentTeam = (team.getCurrentTeam() || {}) as DubateamCurrentTeam

  const token = userInfo.token
  const teamId = Number(currentTeam.team_id)
  const uid = userInfo.uid

  if (!token || !uid || !Number.isFinite(teamId) || teamId <= 0) {
    return null
  }

  const aiSubscriptionExpireAt = resolveAiAssistantSubscriptionExpireAt(readTeamRightsMap())

  return {
    aideKey: AIDE_KEY.DUBATEAM,
    dubateam: {
      token,
      teamId,
      uid,
      aiSubscriptionExpireAt,
    },
    theme: {
      primary: getElColorPrimary(),
      mode: getTheme(),
    },
    locale: getLocale(),
  }
}

export function isBridgeEnvelope (data: unknown): data is BridgeEnvelope {
  if (!data || typeof data !== 'object') return false
  const envelope = data as BridgeEnvelope
  return envelope.channel === BRIDGE_CHANNEL && envelope.version === BRIDGE_VERSION
}

function resolveAideTargetOrigin (customOrigin?: string): string {
  if (customOrigin) return customOrigin
  const base = AIDE_URL.endsWith('/')
    ? AIDE_URL
    : `${AIDE_URL}/`
  return new URL(base).origin
}

/** 宿主侧：父页面 ↔ 助手 iframe 通信 */
export class AideBridgeService {
  private readonly getData: () => BridgeData | null
  private readonly targetOrigin: string
  private iframe: HTMLIFrameElement | null = null
  private readonly handlers = new Map<string, Set<BridgeHandler>>()
  private boundHandleMessage: ((event: MessageEvent) => void) | null = null
  /** 当前 iframe 是否已下发过 forceBoot，避免 load / ready 重复登录 */
  private forceBootSent = false

  constructor (options: AideBridgeServiceOptions) {
    this.getData = options.getData
    this.targetOrigin = resolveAideTargetOrigin(options.targetOrigin)
    this.boundHandleMessage = (event: MessageEvent) => this.handleMessage(event)
    window.addEventListener('message', this.boundHandleMessage)
  }

  bindIframe (iframe: HTMLIFrameElement | null) {
    this.iframe = iframe
    this.forceBootSent = false
    if (!iframe) return

    iframe.addEventListener('load', () => {
      this.pushInitialBoot()
    })
    // iframe 已 load 过时 load 不会再触发；ready 也可能早于监听
    this.pushInitialBoot()
  }

  /** 每个 iframe 只 forceBoot 一次；未允许登录时不发同步 */
  pushInitialBoot () {
    if (!useAideDrawerStore().bootEnabled) return
    if (this.forceBootSent) {
      this.pushSync()
      return
    }
    const sent = this.pushSync({ forceBoot: true })
    if (sent) this.forceBootSent = true
  }

  pushSync (extra?: Partial<BridgeData>): boolean {
    const data = this.getData()
    if (!data) return false
    return this.send(BridgeMessageType.Sync, { ...data, ...extra })
  }

  send (type: string, data?: BridgeData): boolean {
    const iframeWindow = this.iframe?.contentWindow
    if (!iframeWindow) return false

    const aideKey = data?.aideKey ?? AIDE_KEY.DUBATEAM

    const envelope: BridgeEnvelope = {
      channel: BRIDGE_CHANNEL,
      version: BRIDGE_VERSION,
      from: aideKey,
      type,
      data,
    }

    iframeWindow.postMessage(envelope, this.targetOrigin)
    return true
  }

  on (type: string, handler: BridgeHandler) {
    const bucket = this.handlers.get(type) ?? new Set<BridgeHandler>()
    bucket.add(handler)
    this.handlers.set(type, bucket)

    return () => {
      bucket.delete(handler)
    }
  }

  dispose () {
    if (this.boundHandleMessage) {
      window.removeEventListener('message', this.boundHandleMessage)
      this.boundHandleMessage = null
    }
    this.handlers.clear()
    this.iframe = null
  }

  private handleMessage (event: MessageEvent) {
    if (!isBridgeEnvelope(event.data)) return
    if (event.origin !== this.targetOrigin) return
    if (this.iframe?.contentWindow && event.source !== this.iframe.contentWindow) return
    if (event.data.from !== BRIDGE_AIDE_PARTY) return

    if (event.data.type === BridgeMessageType.Ready) {
      this.pushInitialBoot()
    }

    const handlers = this.handlers.get(event.data.type)
    if (!handlers?.size) return

    handlers.forEach((handler) => {
      handler(event.data.data ?? {}, event.data)
    })
  }
}
